import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import ProductKnowledgeRowActions from '../ProductKnowledgeRowActions';
import {
  formatPkbSourceLabel,
  getJiraInfo,
  getKnowledgeItemTitle,
  getKnowledgeItemType,
  getModuleKey,
  getPriorityChipColor,
  getVisiblePageNumbers,
} from './productKnowledgeUtils';
import { SV } from '../../automation/stratovaiTestIds';

const PlaceholderCell = () => (
  <span style={{ color: 'rgba(255,255,255,0.5)' }}>—</span>
);

const ModuleChips = ({ item }) => {
  if (!Array.isArray(item.modules) || item.modules.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {item.modules.slice(0, 3).map((moduleValue, index) => {
        const moduleKey = getModuleKey(moduleValue);
        const isPrimary = item.primary_module && (
          typeof item.primary_module === 'string'
            ? item.primary_module === moduleKey
            : item.primary_module.module_key === moduleKey
        );
        const isAutoGenerated = typeof moduleValue === 'object' && moduleValue.created_by === 'system';

        return (
          <Box key={`${moduleKey || 'module'}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Chip
              label={moduleKey}
              size="small"
              color={isPrimary ? 'primary' : 'default'}
              variant={isPrimary ? 'filled' : 'outlined'}
              sx={{
                fontSize: '0.65rem',
                height: '18px',
                fontWeight: isPrimary ? 600 : 400,
              }}
            />
            {isAutoGenerated && (
              <Chip
                label="Auto"
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.55rem',
                  height: '16px',
                  color: 'text.secondary',
                  borderColor: 'text.secondary',
                  '& .MuiChip-label': { px: 0.5 },
                }}
                title="Auto-generated module"
              />
            )}
          </Box>
        );
      })}
      {item.modules.length > 3 && (
        <Chip
          label={`+${item.modules.length - 3}`}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.65rem', height: '18px' }}
        />
      )}
    </Box>
  );
};

const TagChips = ({ content }) => (
  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
    {content.metadata?.issue_type && (
      <Chip
        label={content.metadata.issue_type}
        size="small"
        color="primary"
        variant="outlined"
        sx={{ fontSize: '0.7rem', height: '20px' }}
      />
    )}
    {content.metadata?.priority && (
      <Chip
        label={content.metadata.priority}
        size="small"
        color={getPriorityChipColor(content.metadata.priority)}
        variant="outlined"
        sx={{ fontSize: '0.7rem', height: '20px' }}
      />
    )}
    {content.project_key && (
      <Chip
        label={content.project_key}
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.7rem', height: '20px' }}
      />
    )}
  </Box>
);

const RowActionsCell = ({
  activeTab,
  item,
  hasGenerated,
  isGenerating,
  onGenerate,
  onRegenerate,
  onPreview,
  onViewImpactedAreas,
  onAnalyzeRequirement,
  onGenerateRCA,
}) => {
  if (activeTab !== 'staged' && activeTab !== 'approved') return null;

  return (
    <ProductKnowledgeRowActions
      item={item}
      hasGenerated={hasGenerated}
      isGenerating={isGenerating}
      canGenerateTestCases={(item?.source || '').toLowerCase() !== 'figma'}
      onGenerate={onGenerate}
      onRegenerate={onRegenerate}
      onPreview={onPreview}
      onViewImpactedAreas={onViewImpactedAreas}
      onAnalyzeRequirement={onAnalyzeRequirement}
      onGenerateRCA={onGenerateRCA}
    />
  );
};

const ParentRow = ({
  item,
  activeTab,
  selectedIds,
  onSelectItem,
  onPreviewItem,
  hierarchyView,
  isSubtaskItem,
  expandedParents,
  onToggleParentExpanded,
  subtasksByParentKey,
  generatedItems,
  generatingTestCases,
  rcaGenerating,
  getJiraKeyForItem,
  onOpenRca,
  onGenerateItem,
  onRegenerateItem,
  onViewImpactedAreas,
  onAnalyzeRequirement,
  onGenerateRca,
}) => {
  const id = item.knowledge_id;
  const content = item.content || {};
  const parentKey = content?.metadata?.parent_key;
  const subtasksCount = content?.metadata?.subtasks_count;
  const { jiraKey, jiraUrl } = getJiraInfo(item);
  const resolvedJiraKey = getJiraKeyForItem(item) || jiraKey;
  const isParent =
    !isSubtaskItem(item) &&
    Number.isFinite(subtasksCount) &&
    subtasksCount > 0 &&
    Boolean(resolvedJiraKey);
  const canExpand = hierarchyView === 'top_level' && isParent;
  const isExpanded = canExpand && expandedParents.has(resolvedJiraKey);
  const childItems = canExpand && resolvedJiraKey ? subtasksByParentKey[resolvedJiraKey] || [] : [];
  const canExpandWithChildren = canExpand && childItems.length > 0;

  const rows = [
    <TableRow key={id} hover sx={{ cursor: 'default' }}>
      <TableCell padding="checkbox">
        <Checkbox
          checked={selectedIds.includes(id)}
          onChange={(event) => {
            event.stopPropagation();
            onSelectItem(id, event.target.checked);
          }}
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {canExpandWithChildren && (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onToggleParentExpanded(resolvedJiraKey);
              }}
              sx={{ p: 0.25 }}
              aria-label={isExpanded ? 'Collapse subtasks' : 'Expand subtasks'}
            >
              {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          )}
          <Button
            variant="text"
            size="small"
            data-testid={SV.pk.rowOpenTitle}
            onClick={(event) => {
              event.stopPropagation();
              onPreviewItem(item);
            }}
            sx={{ textTransform: 'none', minWidth: 'auto', px: 0 }}
          >
            {getKnowledgeItemTitle(item)}
          </Button>
          {content?.metadata?.id && String(content.metadata.id).startsWith('JIRA:') && (
            <Chip
              label={String(content.metadata.id).split(':')[1]}
              size="small"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                if (jiraUrl) window.open(jiraUrl, '_blank');
              }}
              sx={{ fontSize: '0.7rem', height: '20px', cursor: jiraUrl ? 'pointer' : 'default' }}
            />
          )}
          {item.has_figma && (
            <Chip
              label="Designs"
              size="small"
              color="primary"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                onPreviewItem(item);
              }}
              sx={{
                fontSize: '0.7rem',
                height: '20px',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                },
              }}
            />
          )}
          <ModuleChips item={item} />
          {(() => {
            const issueType = content.metadata?.issue_type?.toLowerCase() || '';
            if (issueType !== 'bug' || !resolvedJiraKey) return null;
            const rcaStatus = item.rca_status || 'none';
            if (rcaStatus === 'none') return null;
            const isStale = rcaStatus === 'stale';
            return (
              <Chip
                label="RCA"
                size="small"
                color={isStale ? 'warning' : 'error'}
                variant="filled"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenRca(item);
                }}
                icon={isStale ? <WarningIcon sx={{ fontSize: '0.9rem !important' }} /> : undefined}
                sx={{
                  fontSize: '0.7rem',
                  height: '20px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: isStale ? 'warning.dark' : 'error.dark',
                  },
                  ...(isStale && {
                    border: '1px solid',
                    borderColor: 'warning.main',
                    bgcolor: 'warning.light',
                  }),
                }}
                title={
                  isStale
                    ? 'Root Cause Analysis (Stale) - Click to view and regenerate'
                    : 'Root Cause Analysis - Click to view details'
                }
              />
            );
          })()}
          {item.pii_masking_alert && (
            <Chip
              label="PII_MASKED"
              size="small"
              color="warning"
              variant="outlined"
              icon={<WarningIcon sx={{ fontSize: '0.9rem !important' }} />}
              sx={{
                fontSize: '0.7rem',
                height: '20px',
                fontWeight: 'bold',
                borderColor: 'warning.main',
                bgcolor: 'warning.light',
                color: 'warning.dark',
              }}
              title="PII masking may have caused understanding gaps. Some sensitive information was masked."
            />
          )}
          {item.times_generated > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Chip
                label={`Generated ${item.times_generated}×`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
              {item.last_generated_at && (
                <Chip
                  label={`Last: ${new Date(item.last_generated_at).toLocaleDateString()}`}
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              )}
              {item.test_cases_generated_count > 0 && (
                <Chip
                  label={`Total TCs: ${item.test_cases_generated_count}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              )}
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell>{getKnowledgeItemType(item)}</TableCell>
      <TableCell>{formatPkbSourceLabel(item.source)}</TableCell>
      <TableCell>
        {parentKey ? (
          <Chip
            size="small"
            variant="outlined"
            label={`Child of ${parentKey}`}
            sx={{ fontSize: '0.7rem', height: '20px' }}
          />
        ) : Number.isFinite(subtasksCount) && subtasksCount > 0 ? (
          <Chip
            size="small"
            variant="outlined"
            label={`Parent of ${subtasksCount}`}
            sx={{ fontSize: '0.7rem', height: '20px' }}
          />
        ) : (
          <PlaceholderCell />
        )}
      </TableCell>
      <TableCell>
        <TagChips content={content} />
      </TableCell>
      <TableCell>{new Date(item.created_at).toLocaleDateString()}</TableCell>
      <TableCell>
        <RowActionsCell
          activeTab={activeTab}
          item={item}
          hasGenerated={generatedItems.has(id)}
          isGenerating={generatingTestCases[id] || rcaGenerating}
          onGenerate={onGenerateItem}
          onRegenerate={onRegenerateItem}
          onPreview={onPreviewItem}
          onViewImpactedAreas={onViewImpactedAreas}
          onAnalyzeRequirement={onAnalyzeRequirement}
          onGenerateRCA={onGenerateRca}
        />
      </TableCell>
    </TableRow>,
  ];

  if (isExpanded && childItems.length) {
    for (const child of childItems) {
      const childId = child.knowledge_id;
      const childContent = child.content || {};
      const childJiraKey = resolvedJiraKey || getJiraInfo(child).jiraKey;
      const { jiraUrl: childJiraUrl } = getJiraInfo(child);

      rows.push(
        <TableRow
          key={`${id}__child__${childId}`}
          hover
          sx={{ cursor: 'default', bgcolor: 'rgba(255,255,255,0.03)' }}
        >
          <TableCell padding="checkbox">
            <Checkbox
              checked={selectedIds.includes(childId)}
              onChange={(event) => {
                event.stopPropagation();
                onSelectItem(childId, event.target.checked);
              }}
            />
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pl: 3 }}>
              <Chip
                size="small"
                variant="outlined"
                label="Subtask"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
              <Button
                variant="text"
                size="small"
                data-testid={SV.pk.rowOpenTitle}
                onClick={(event) => {
                  event.stopPropagation();
                  onPreviewItem(child);
                }}
                sx={{ textTransform: 'none', minWidth: 'auto', px: 0 }}
              >
                {getKnowledgeItemTitle(child)}
              </Button>
              {childContent?.metadata?.id && String(childContent.metadata.id).startsWith('JIRA:') && (
                <Chip
                  label={String(childContent.metadata.id).split(':')[1]}
                  size="small"
                  variant="outlined"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (childJiraUrl) window.open(childJiraUrl, '_blank');
                  }}
                  sx={{ fontSize: '0.7rem', height: '20px', cursor: childJiraUrl ? 'pointer' : 'default' }}
                />
              )}
            </Box>
          </TableCell>
          <TableCell>{getKnowledgeItemType(child)}</TableCell>
          <TableCell>{formatPkbSourceLabel(child.source)}</TableCell>
          <TableCell>
            <Chip
              size="small"
              variant="outlined"
              label={`Child of ${childContent?.metadata?.parent_key || childJiraKey}`}
              sx={{ fontSize: '0.7rem', height: '20px' }}
            />
          </TableCell>
          <TableCell>
            <TagChips content={childContent} />
          </TableCell>
          <TableCell>{new Date(child.created_at).toLocaleDateString()}</TableCell>
          <TableCell>
            <RowActionsCell
              activeTab={activeTab}
              item={child}
              hasGenerated={generatedItems.has(childId)}
              isGenerating={generatingTestCases[childId] || rcaGenerating}
              onGenerate={onGenerateItem}
              onRegenerate={onRegenerateItem}
              onPreview={onPreviewItem}
              onViewImpactedAreas={onViewImpactedAreas}
              onAnalyzeRequirement={onAnalyzeRequirement}
              onGenerateRCA={onGenerateRca}
            />
          </TableCell>
        </TableRow>
      );
    }
  }

  return rows;
};

const ProductKnowledgeTable = ({
  activeTab,
  sortedItems,
  emptyStateMessage,
  paginatedItems,
  selectedIds,
  sortConfig,
  onSort,
  onSelectAll,
  onSelectItem,
  onPreviewItem,
  hierarchyView,
  isSubtaskItem,
  subtasksByParentKey,
  expandedParents,
  onToggleParentExpanded,
  generatedItems,
  generatingTestCases,
  rcaGenerating,
  getJiraKeyForItem,
  onOpenRca,
  onGenerateItem,
  onRegenerateItem,
  onViewImpactedAreas,
  onAnalyzeRequirement,
  onGenerateRca,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  if (sortedItems.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        {emptyStateMessage}
      </Typography>
    );
  }

  const visiblePageNumbers = getVisiblePageNumbers(currentPage, totalPages);

  return (
    <>
      <Table data-testid={SV.pk.table} size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={selectedIds.length === paginatedItems.length && paginatedItems.length > 0}
                indeterminate={selectedIds.length > 0 && selectedIds.length < paginatedItems.length}
                onChange={(event) => onSelectAll(event.target.checked)}
              />
            </TableCell>
            <TableCell onClick={() => onSort('title')} sx={{ cursor: 'pointer', userSelect: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Title
                {sortConfig.key === 'title' && <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>}
              </Box>
            </TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Hierarchy</TableCell>
            <TableCell>Tags</TableCell>
            <TableCell>Uploaded</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedItems.flatMap((item) => (
            <ParentRow
              key={item.knowledge_id}
              item={item}
              activeTab={activeTab}
              selectedIds={selectedIds}
              onSelectItem={onSelectItem}
              onPreviewItem={onPreviewItem}
              hierarchyView={hierarchyView}
              isSubtaskItem={isSubtaskItem}
              expandedParents={expandedParents}
              onToggleParentExpanded={onToggleParentExpanded}
              subtasksByParentKey={subtasksByParentKey}
              generatedItems={generatedItems}
              generatingTestCases={generatingTestCases}
              rcaGenerating={rcaGenerating}
              getJiraKeyForItem={getJiraKeyForItem}
              onOpenRca={onOpenRca}
              onGenerateItem={onGenerateItem}
              onRegenerateItem={onRegenerateItem}
              onViewImpactedAreas={onViewImpactedAreas}
              onAnalyzeRequirement={onAnalyzeRequirement}
              onGenerateRca={onGenerateRca}
            />
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Page {currentPage} of {totalPages}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              variant="outlined"
              size="small"
            >
              ← Previous
            </Button>

            {visiblePageNumbers.map((pageNumber) => (
              <Button
                key={pageNumber}
                onClick={() => onPageChange(pageNumber)}
                variant={currentPage === pageNumber ? 'contained' : 'outlined'}
                size="small"
                sx={{ minWidth: 40 }}
              >
                {pageNumber}
              </Button>
            ))}

            <Button
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              variant="outlined"
              size="small"
            >
              Next →
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ProductKnowledgeTable;
